// src/components/editor/ParameterEditor.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../common/Button';
import { Input, TextArea } from '../common/Input';
import { theme } from '../../styles/theme';
import { Parameter } from '../../types';

const ParameterContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background};
`;

const ParametersHeader = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const ParametersGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ParameterItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs};
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  min-width: 0;
`;

const ParameterName = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  min-width: 20px;
  text-align: center;
  font-size: 0.85rem;
`;

const RemoveButton = styled(Button)`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  font-size: 0.8rem;
  flex-shrink: 0;
`;

interface ParameterEditorProps {
  parameters?: Parameter[];
  onChange: (parameters: Parameter[]) => void;
  title?: string;
}

export const ParameterEditor: React.FC<ParameterEditorProps> = ({
  parameters = [],
  onChange,
  title = 'Мета',
}) => {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [newParamName, setNewParamName] = useState('');
  const [newParamValue, setNewParamValue] = useState('');

  const availableLetters = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(97 + i)
  ).filter(letter => !parameters.some(p => p.name === letter));

  const handleAddParameter = () => {
    if (newParamName && newParamValue) {
      onChange([
        ...parameters,
        { id: `param_${Date.now()}`, name: newParamName, value: newParamValue },
      ]);
      setNewParamName('');
      setNewParamValue('');
      setShowAddMenu(false);
    }
  };

  const handleRemoveParameter = (paramId: string) => {
    onChange(parameters.filter(p => p.id !== paramId));
  };

  const handleUpdateParameterValue = (paramId: string, value: string) => {
    onChange(parameters.map(p => (p.id === paramId ? { ...p, value } : p)));
  };

  return (
    <ParameterContainer>
      <ParametersHeader>{title}</ParametersHeader>

      {parameters.length > 0 && (
        <ParametersGrid>
          {parameters.map((param) => (
            <ParameterItem key={param.id}>
              <ParameterName>{param.name}</ParameterName>
              <TextArea
                value={param.value}
                onChange={(e) => handleUpdateParameterValue(param.id, e.target.value)}
                placeholder={`Значение ${param.name}`}
                maxLength={2000}
                rows={3}
                style={{ flex: 1, minWidth: 0, fontSize: '0.85rem', resize: 'vertical' }}
              />
              <RemoveButton
                variant="outline"
                size="sm"
                onClick={() => handleRemoveParameter(param.id)}
              >
                ×
              </RemoveButton>
            </ParameterItem>
          ))}
        </ParametersGrid>
      )}

      {showAddMenu && (
        <div style={{ marginBottom: theme.spacing.sm, padding: theme.spacing.sm, background: theme.colors.cardBackground, borderRadius: theme.borderRadius.sm, border: `1px solid ${theme.colors.border}` }}>
          <div style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.sm }}>
            <div style={{ flex: '0 0 100px' }}>
              <label style={{ display: 'block', marginBottom: theme.spacing.xs, fontWeight: 600, fontSize: '0.85rem' }}>
                Буква (a-z):
              </label>
              <select
                value={newParamName}
                onChange={(e) => setNewParamName(e.target.value)}
                style={{ width: '100%', padding: theme.spacing.xs, borderRadius: theme.borderRadius.sm, border: `1px solid ${theme.colors.border}` }}
              >
                <option value="">—</option>
                {availableLetters.map(letter => (
                  <option key={letter} value={letter}>{letter}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: theme.spacing.xs, fontWeight: 600, fontSize: '0.85rem' }}>
                Значение:
              </label>
              <TextArea
                value={newParamValue}
                onChange={(e) => setNewParamValue(e.target.value)}
                placeholder="Введите значение..."
                maxLength={2000}
                rows={3}
                style={{ flex: 1, minWidth: 0, width: '100%', fontSize: '0.85rem', resize: 'vertical' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddParameter}
              disabled={!newParamName || !newParamValue}
            >
              Сохранить
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowAddMenu(false); setNewParamName(''); setNewParamValue(''); }}
            >
              Отмена
            </Button>
          </div>
        </div>
      )}

      {!showAddMenu && (
        <Button
          variant="text"
          size="sm"
          onClick={() => setShowAddMenu(true)}
        >
          + Добавить мета
        </Button>
      )}
    </ParameterContainer>
  );
};
